// UI string lookup (`t`) and locale-aware formatters.
//
// All catalogs are statically imported so SSR / prerender bundle them into
// the build (no async runtime fetch). The `t()` lookup walks dot-notation
// keys (e.g. "home.headline" or "snapshot.asking_price") with a deep
// `{var}` substitution pass and falls back to the English value when a key
// is missing in a non-default locale — that fallback should never fire in
// production but keeps the UI usable while a translator iterates.

import { useMemo } from "react";
import enCatalog from "@/locales/en.json";
import frCatalog from "@/locales/fr.json";
import esCatalog from "@/locales/es.json";
import deCatalog from "@/locales/de.json";
import nlCatalog from "@/locales/nl.json";
import ruCatalog from "@/locales/ru.json";
import { useLocale } from "./locale-context";
import { LOCALE_INTL, LOCALE_CURRENCY, MAD_RATE, type Locale } from "./locale";

type Catalog = typeof enCatalog;

const CATALOGS: Record<Locale, Catalog> = {
  en: enCatalog,
  fr: frCatalog as Catalog,
  es: esCatalog as Catalog,
  de: deCatalog as Catalog,
  nl: nlCatalog as Catalog,
  ru: ruCatalog as Catalog,
};

function getByPath(obj: unknown, path: string): unknown {
  const parts = path.split(".");
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return undefined;
    }
  }
  return cur;
}

function substitute(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, k) => {
    const v = vars[k];
    return v == null ? m : String(v);
  });
}

export function getCatalog(locale: Locale): Catalog {
  return CATALOGS[locale] ?? CATALOGS.en;
}

/**
 * Translate a dot-notation key. Falls back to English when the key is
 * missing in the target locale, then to the key itself if even English
 * lacks it. Variables in the template are substituted last.
 */
export function translate(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const localized = getByPath(CATALOGS[locale] ?? CATALOGS.en, key);
  if (typeof localized === "string") return substitute(localized, vars);
  const en = getByPath(CATALOGS.en, key);
  if (typeof en === "string") return substitute(en, vars);
  return key;
}

/**
 * React hook bound to the active locale. Returns a stable `t()` function
 * (memoised per locale) so consumers can use it as a dependency without
 * re-rendering on every render.
 */
export function useT() {
  const { locale } = useLocale();
  return useMemo(() => {
    const t = (key: string, vars?: Record<string, string | number>) =>
      translate(locale, key, vars);
    t.locale = locale;
    return t;
  }, [locale]);
}

// ---------- locale-aware Intl formatters ----------

export function formatMad(n: number | null | undefined, locale: Locale): string {
  if (n == null) return "—";
  const currency = LOCALE_CURRENCY[locale] ?? "USD";
  const rate = MAD_RATE[currency] ?? 1;
  const converted = n * rate;
  try {
    return new Intl.NumberFormat(LOCALE_INTL[locale], {
      style: "currency",
      currency,
      maximumFractionDigits: converted >= 1_000_000 ? 1 : 0,
      notation: converted >= 1_000_000 ? "compact" : "standard",
    }).format(converted);
  } catch {
    return `${currency} ${Math.round(converted).toLocaleString(LOCALE_INTL[locale])}`;
  }
}

export function formatMadPerM2(n: number | null | undefined, locale: Locale): string {
  if (n == null) return "—";
  const currency = LOCALE_CURRENCY[locale] ?? "USD";
  const rate = MAD_RATE[currency] ?? 1;
  const converted = n * rate;
  try {
    const formatted = new Intl.NumberFormat(LOCALE_INTL[locale], {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(converted);
    return `${formatted}/m²`;
  } catch {
    return `${Math.round(converted)} ${currency}/m²`;
  }
}

/**
 * Short compact formatter for cards and signal panels.
 * Converts MAD → locale currency, then formats compactly.
 *   en:  "$120K" / "$1.2M"
 *   fr:  "110 K€" / "1,1M€"
 *   ru:  "1,1M ₽"
 */
export function formatMadShort(n: number | null | undefined, locale: Locale): string {
  if (n == null || n === 0) return "—";
  const currency = LOCALE_CURRENCY[locale] ?? "USD";
  const rate = MAD_RATE[currency] ?? 1;
  const converted = n * rate;
  try {
    return new Intl.NumberFormat(LOCALE_INTL[locale], {
      style: "currency",
      currency,
      maximumFractionDigits: 1,
      notation: "compact",
      compactDisplay: "short",
    }).format(converted);
  } catch {
    return `${currency} ${Math.round(converted / 1000)}K`;
  }
}

export function formatNumber(n: number | null | undefined, locale: Locale): string {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat(LOCALE_INTL[locale]).format(n);
  } catch {
    return String(n);
  }
}

/**
 * Locale-aware "X minutes / hours / days ago" relative-time string. Uses
 * `Intl.RelativeTimeFormat` so plural rules and unit names come from the
 * platform — callers don't need per-locale templates.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  locale: Locale,
): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const diffMs = then - Date.now();
  const absMs = Math.abs(diffMs);
  try {
    const rtf = new Intl.RelativeTimeFormat(LOCALE_INTL[locale], {
      numeric: "auto",
      style: "short",
    });
    const minute = 60_000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;
    const month = 30 * day;
    const year = 365 * day;
    if (absMs < hour) {
      return rtf.format(Math.round(diffMs / minute), "minute");
    }
    if (absMs < day) {
      return rtf.format(Math.round(diffMs / hour), "hour");
    }
    if (absMs < week) {
      return rtf.format(Math.round(diffMs / day), "day");
    }
    if (absMs < month) {
      return rtf.format(Math.round(diffMs / week), "week");
    }
    if (absMs < year) {
      return rtf.format(Math.round(diffMs / month), "month");
    }
    return rtf.format(Math.round(diffMs / year), "year");
  } catch {
    return null;
  }
}

export function formatDate(iso: string | null | undefined, locale: Locale): string | null {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(LOCALE_INTL[locale], {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(iso));
  } catch {
    return null;
  }
}

/**
 * React hook returning locale-aware formatters bound to the active locale.
 */
export function useFmt() {
  const { locale } = useLocale();
  return useMemo(
    () => ({
      mad: (n: number | null | undefined) => formatMad(n, locale),
      madPerM2: (n: number | null | undefined) => formatMadPerM2(n, locale),
      number: (n: number | null | undefined) => formatNumber(n, locale),
      date: (iso: string | null | undefined) => formatDate(iso, locale),
    }),
    [locale],
  );
}
